import {
  CellClassParams,
  CellClickedEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  Module,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, OnDestroy, OnInit } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  EditableCheckboxRenderer,
  generateMdiIcon,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { deduceValidityStatusCellClass } from '@helios/create-transactions-modal'
import { BlankSessionService } from '@helios/sessions'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  getInlineEditColumnType,
  InlineEditDataType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import {
  buildCopyHeaders,
  filterRegistryByAvailableCommands,
  ProcessingColumn,
  ProcessingColumnDataType,
  ProcessingCommandRegistryEntry,
  ProcessingFlagColumn,
} from '../models/corporate-action-processing-registry'
import {
  CorporateActionProcessingRowResult,
  CorporateActionsProcessingBatchRequest,
  CorporateActionsProcessingBatchResponse,
  isRowAccepted,
  isRowQueued,
  isRowRetired,
  isRowUnconfirmed,
  ProcessingGridRow,
  ProcessingRowValues,
  ROW_STATUS_ACCEPTED,
  ROW_STATUS_UNCONFIRMED,
} from '../models/corporate-action-processing.model'
import { CorporateActionsProcessingService } from '../services/corporate-actions-processing.service'
import { parsePastedProcessingRows } from '../util/corporate-actions-processing-paste.util'
import { buildProcessingBatchRequest } from '../util/corporate-actions-processing-request.util'
import {
  isRowEmpty,
  PROCESSING_VALIDATED_FIELDS,
  toFieldErrorMap,
  validateProcessingRow,
} from '../validators/corporate-actions-processing-validator'
import {
  ProcessingErrorTooltip,
  ProcessingTooltipParams,
} from './processing-error-tooltip'
import { ProcessingSubmitConfirmationModal } from './processing-submit-confirmation-modal'

export const VALIDITY_STATUS_COL_ID = 'validityStatus'
export const ROW_NUMBER_COL_ID = 'rowNumber'
export const SUBMISSION_STATUS_COL_ID = 'submissionStatus'
export const DELETE_COL_ID = 'deleteRow'

/**
 * Minimum width for an editable DATA column, in px.
 *
 * Exists only to override the icon-sized `defaultColDef.minWidth`
 * (`heliosDefaultColWidth.ValidityStatusCol`) that these columns would otherwise
 * inherit. Deliberately below the narrowest width the registry declares (100px)
 * so it never changes a rendered width — it is a floor for manual resizing, not a
 * layout input.
 */
export const PROCESSING_DATA_COL_MIN_WIDTH = 90

/**
 * Clipboard text is only treated as a block of rows when it actually contains a
 * tab or a newline. Anything else is a scalar destined for a cell editor.
 */
const ROW_BLOCK_PATTERN = /[\t\r\n]/

/**
 * Trailing line break of a clipboard payload, in any of the three forms.
 *
 * Excel and Google Sheets append ONE to every copy — including a copy of a SINGLE
 * cell — so it carries no information about whether the payload is a row block,
 * and `ROW_BLOCK_PATTERN` must not see it. See `stripTrailingNewline`.
 */
const TRAILING_NEWLINE_PATTERN = /\r\n$|[\r\n]$/

/**
 * Drop ONE trailing line break from clipboard text before deciding whether it is
 * a block of rows.
 *
 * Without this, Excel's own trailing CRLF made every single-cell copy look like a
 * row block: `"123\r\n"` passed `ROW_BLOCK_PATTERN`,
 * `parsePastedProcessingRows` dropped the blank line and returned ONE row, and
 * the paste appended a row with only Account ID filled — immediately invalid, so
 * `invalidRowCount > 0` disabled Submit for the WHOLE grid. That is the exact
 * inverse of the documented intent ("leave scalars to the cell editor"), and it
 * fired on the most common clipboard action in a spreadsheet grid.
 *
 * Only one break is removed, so a genuine multi-row block that also ends in a
 * newline still tests (and parses) as a block.
 */
function stripTrailingNewline(clipboardText: string): string {
  return clipboardText.replace(TRAILING_NEWLINE_PATTERN, '')
}

/**
 * Copy for a failed `availableCommands` fetch.
 *
 * Deliberately does NOT say "reopen this tab to retry". That only ever worked
 * while ngbNav destroyed the pane on a tab switch and re-ran `ngOnInit`; the
 * `[destroyOnHide]="!processingTabActivated"` latch makes the child KEEP-ALIVE
 * after first activation, so `ngOnInit` runs exactly once and a
 * tab-3 -> tab-1 -> tab-3 round trip refetches nothing (the panel behaviour spec
 * asserts precisely that: "issues NO second availableCommands request"). Closing
 * and re-adding the panel does not help either, because the service is
 * `providedIn: 'root'`. An instruction the product cannot honour is worse than
 * no instruction, so the message points at the real affordance — the Retry
 * button, wired to `retryLoadCommands()`.
 */
export const COMMANDS_LOAD_ERROR =
  'Available corporate action processing commands could not be loaded. Use Retry to try again.'

/**
 * Copy for a failure the client CAN attribute: the server rejected the request
 * outright (a 4xx other than 408), so no command was queued and the rows stay
 * editable and submittable. The whole-batch Jackson rejection documented in the
 * PVAL-25926 contract lands here, which is why this path must NOT retire the
 * user's rows — doing so would destroy a batch we know was never processed.
 */
export const SUBMIT_REJECTED_ERROR =
  'The batch was rejected before any command was queued. Review the rows and try again.'

/**
 * Copy for a failure the client CANNOT attribute — a timeout, a 504, a dropped
 * connection, any 5xx. The request may well have been queued server-side before
 * the response was lost.
 *
 * Deliberately does NOT claim nothing was queued (the previous wording did, and
 * that is a fact the client has no way to know — it actively walked the user
 * into a duplicate POST of a financial corporate action). It points at the
 * Commands drawer and Jobs panel instead, which are the surfaces that can
 * actually answer the question.
 */
export const SUBMIT_UNCONFIRMED_ERROR =
  'The batch could not be confirmed — the rows were sent but no response came back, so some commands may already have been queued. Check the Commands drawer and the Jobs panel before resubmitting.'

/** Nothing left the browser: creating/activating the session failed. */
export const SESSION_ERROR =
  'A Helios session could not be started, so nothing was submitted. Try again.'

/**
 * Copy for a failure BEFORE transmission that is not the session: building the
 * wire body threw.
 *
 * Same class as `SESSION_ERROR` — nothing left the browser, so the rows are
 * untouched and stay submittable — but it must not borrow that message, because
 * "a Helios session could not be started" would be a false statement about what
 * happened. `buildProcessingBatchRequest` is pure and defensive, so this is
 * defence in depth rather than an observed failure; the point is that a coercion
 * throw is NOT routed to `handleSubmitFailure`, which would retire a batch that
 * was never sent.
 */
export const SUBMIT_NOT_SENT_ERROR =
  'The batch could not be prepared, so nothing was submitted. Review the rows and try again.'

/**
 * Copy for a throw while APPLYING a response that DID arrive.
 *
 * Deliberately distinct from `SUBMIT_UNCONFIRMED_ERROR`: that message says no
 * response came back, which on this path is simply untrue — the POST succeeded
 * and per-row results were being written onto the grid when something on the
 * apply/render path threw (a `redrawRows` against a torn-down grid, the toast
 * service, an ag-grid internal). Telling the user "no response came back" about
 * a batch that came back with per-row results is what made this worth its own
 * message, and the grid may now be showing a partially-applied response, so the
 * copy points at the surfaces that can settle it rather than inviting a resubmit.
 */
export const SUBMIT_RESULTS_ERROR =
  'The batch was sent and the server responded, but its results could not all be applied to the grid. Check the Commands drawer and the Jobs panel before resubmitting.'

/**
 * Copy for a `200` in which the server PROVED every transmitted row rejected —
 * `accepted === 0 && unconfirmed === 0`.
 *
 * Without this the batch was the one failure shape that produced no inline
 * message and no toast at all: neither the accepted nor the unconfirmed toast
 * fires, `submitError` stayed `''`, and the only signal was the footer summary
 * quietly reading "0 row(s) submitted and queued, N rejected". Every other
 * failure path on this tab sets both an inline message and a toast, so this one
 * read as "nothing happened" on a click that did reach the server. Deliberately
 * says nothing was queued, which on this path the client genuinely knows: every
 * row came back a declared rejection status with no commandId.
 */
export const SUBMIT_ALL_REJECTED_ERROR =
  'No command was queued — the server rejected every row. Review the message on each row and try again.'

/**
 * Copy for a clipboard write the browser refused (non-secure context, denied
 * permission, unfocused document). Names the fallback that always works, since
 * the headers are on screen in the grid's own header row.
 */
export const COPY_HEADERS_ERROR =
  'The column headers could not be copied to the clipboard. Copy them from the grid header row instead.'

/** Row-level footer note for a row whose outcome the response never reported. */
export const UNCONFIRMED_ROW_MESSAGE =
  'Sent, but the server reported no outcome for this row. Check the Commands drawer before resubmitting.'

export const NO_COMMANDS_AVAILABLE =
  'You do not have a Corporate Actions Processing policy for any command type, so there is nothing to enter here.'

/**
 * Corporate Actions Processing tab (PVAL-25952) — an editable, spreadsheet-style
 * grid where one row equals one command.
 *
 * Deliberately a CHILD component rather than logic inside
 * `CorporateActionsPanelComponent`:
 *
 * - The Clearwater/ICE-unchanged gate is reporter-confirmed, so the shared-surface
 *   delta is exactly one `<li [ngbNavItem]="3">` block plus one additive
 *   `else if (tabId === 3)` branch, and neither
 *   `corporate-actions-panel.component.spec.ts` nor
 *   `corporate-actions-panel-ice.behavior.spec.ts` is modified, so those two
 *   files are byte-identical to their state before this branch and nothing here
 *   can have loosened a Clearwater or ICE assertion. Both of those suites also
 *   PASS — `yarn nx run corporate-actions:test` is green end to end on this
 *   branch — so they are a live regression gate, not merely an unmodified-file
 *   gate. (Suite/test counts deliberately live in ONE place, the lib's
 *   `CLAUDE.md`, rather than in docblocks that rot on the next commit.) The
 *   panel-level coverage this branch ADDS is
 *   `corporate-actions-panel-processing.behavior.spec.ts`: it covers the
 *   `tabId === 3` branch and asserts at DOM level that the tab anchor renders and
 *   that this component is instantiated on activation.
 * - The `GET availableCommands` fetch lives in THIS component's `ngOnInit`, not
 *   the panel's. The panel's own init path drives the Clearwater tab
 *   (`corporateActionUserPolicy()`, focused-issue/account selectors); a failure
 *   there would degrade the Clearwater tab, and the endpoint 404s until
 *   PVAL-26137 deploys. Because `ngbNav` instantiates `ngbNavContent` lazily on
 *   first activation, a user who never opens this tab never issues the request.
 *
 * All state is component-local plain fields — no NgRx, and nothing is added to
 * `CorporateActionsPanelState` (which is persisted GoldenLayout config holding
 * only resize-handle positions).
 *
 * ONE exception (PVAL-26303 #2): the row set and per-cell error map are mirrored
 * into the `providedIn: 'root'` service so they survive this component being
 * destroyed, which a panel stack-tab switch and a panel close both do. In
 * memory only, so a reload starts empty. See `RetainedProcessingGridState`.
 */
@Component({
  selector: 'hs-corporate-actions-processing',
  templateUrl: './corporate-actions-processing.component.html',
  styleUrls: ['./corporate-actions-processing.component.scss'],
})
export class CorporateActionsProcessingComponent implements OnInit, OnDestroy {
  availableEntries: ProcessingCommandRegistryEntry[] = []
  selectedCommandType = ''
  rowData: ProcessingGridRow[] = []
  colDefs: ColDef[] = []
  gridOptions: GridOptions = this.createGridOptions()
  gridModules: Module[] = [RangeSelectionModule]

  /** `${rowId}-${field}` -> messages. Drives red cells + per-cell tooltips. */
  cellLevelErrorMessageMap = new Map<string, string[]>()

  loadingCommands = false
  commandsError = ''
  submitError = ''
  submitting = false
  /**
   * Rows transmitted by the current submit, until its response lands. Held in
   * the retained state by reference, since a submit triggers no store write.
   * `submitting` cannot do this job: it dies with the component, so a rebuild
   * starts unlocked while the old instance's POST is still in flight.
   */
  private inFlightRowIds = new Set<string>()
  readonly noCommandsMessage = NO_COMMANDS_AVAILABLE
  /** Row results the client could not attribute to a row in the sent batch. */
  unmappedResultCount = 0
  lastSubmissionSummary = ''

  /** @internal public for testing */
  gridApi?: AgGridApi

  private rowSequence = 0
  private readonly instanceId = Math.random().toString(36).slice(2, 8)
  private readonly destroyed$ = new Subject<void>()

  constructor(
    private readonly processingService: CorporateActionsProcessingService,
    private readonly blankSessionService: BlankSessionService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadAvailableCommands()
  }

  /**
   * Re-run the `availableCommands` fetch after a failure.
   *
   * This is the tab's ONLY recovery path, which is why it is a real control and
   * not a documented gesture. `ngOnInit` runs exactly once — the panel keeps this
   * child alive after first activation (`[destroyOnHide]="!processingTabActivated"`)
   * precisely so a tab switch cannot destroy the retirement record — so nothing
   * in the UI re-enters the load on its own. And because
   * `CorporateActionsProcessingService` is `providedIn: 'root'`, even closing and
   * re-adding the whole panel reuses the same cached stream. Without this button
   * a user who opened the tab before PVAL-26137 deployed would be stuck on the
   * error for the rest of the page session.
   *
   * Safe to press repeatedly: the service's `catchError`-nulls-the-cache idiom
   * means the previous failure is not cached, and the in-flight guard below stops
   * a double-press issuing two requests.
   */
  retryLoadCommands(): void {
    if (this.loadingCommands) {
      return
    }
    this.loadAvailableCommands()
  }

  private loadAvailableCommands(): void {
    this.loadingCommands = true
    this.commandsError = ''
    this.processingService
      .getAvailableCommands()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(
        commands => this.applyAvailableCommands(commands),
        () => {
          // Handled locally: an empty registry plus an inline message and a Retry
          // button keeps the tab (and the rest of the panel) usable while
          // `processing/availableCommands` still 404s pre-PVAL-26137.
          //
          // This callback only fires because the service sets
          // `HANDLE_ERROR_AND_THROW`; the app-wide `ErrorInterceptor` otherwise
          // converts every HTTP failure into `EMPTY`, which would leave
          // `loadingCommands` true forever and this whole branch dead.
          this.availableEntries = []
          this.selectedCommandType = ''
          this.rowData = []
          this.loadingCommands = false
          this.commandsError = COMMANDS_LOAD_ERROR
        },
      )
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  private applyAvailableCommands(commands: string[]): void {
    this.availableEntries = filterRegistryByAvailableCommands(commands)
    this.loadingCommands = false
    this.commandsError = ''
    if (this.availableEntries.length > 0) {
      const commandType = this.availableEntries[0].commandType
      // Two calls, deliberately (PVAL-26303 #2): `setCommandType` rebuilds
      // COLUMNS, `initialiseRowsFor` restores ROWS. `resetRows()` here would
      // save a blank grid over the user's retained rows on every rebuild.
      this.setCommandType(commandType)
      this.initialiseRowsFor(commandType)
    }
  }

  get activeEntry(): ProcessingCommandRegistryEntry | undefined {
    return this.availableEntries.find(
      entry => entry.commandType === this.selectedCommandType,
    )
  }

  get columns(): ProcessingColumn[] {
    return this.activeEntry?.columns ?? []
  }

  /**
   * Boolean checkbox columns for the active command type.
   *
   * Kept out of `columns` deliberately: `columns` is the positional contract for
   * Excel paste and Copy Headers, and these are not spreadsheet-pasted values.
   */
  get flagColumns(): ProcessingFlagColumn[] {
    return this.activeEntry?.flagColumns ?? []
  }

  /** Rows the user has actually started filling in. */
  get activeRows(): ProcessingGridRow[] {
    return this.rowData.filter(row => !isRowEmpty(row))
  }

  /**
   * Rows a Submit may still send: populated rows that have NOT been retired.
   *
   * A row is retired once the server confirms it was queued (`ACCEPTED`), once it
   * was transmitted with an unknown outcome (`UNCONFIRMED`), once the server
   * hands back a `commandId` at all, or once it comes back with any status this
   * UI does not recognise as a PROVEN rejection. This is the ONLY collection the
   * batch is built from, and every one of those states must be excluded for the
   * same reason: re-POSTing any of them can duplicate an already-queued financial
   * command, and nothing catches that duplicate synchronously because
   * perseus-worker's checks are asynchronous. An unknown-outcome row is exactly
   * as dangerous as a known-queued one — the whole point is that the client
   * cannot tell them apart, so it must not guess in the direction that re-sends.
   */
  get submittableRows(): ProcessingGridRow[] {
    return this.activeRows.filter(row => !isRowRetired(row))
  }

  /** Populated rows already queued server-side; retired from further submits. */
  get queuedRowCount(): number {
    return this.activeRows.filter(row => isRowAccepted(row)).length
  }

  /**
   * Populated rows that were sent but whose outcome is NOT a confirmed queueing.
   *
   * Also retired — surfaced separately so the footer can tell the user to confirm
   * via the Commands drawer rather than silently swallowing them. Covers the
   * client-side sentinel, a server-returned literal `UNCONFIRMED` and any status
   * this UI does not recognise as a proven rejection: all three are outcomes the
   * client cannot report as either queued or cleanly failed.
   */
  get unconfirmedRowCount(): number {
    return this.activeRows.filter(row => isRowUnconfirmed(row)).length
  }

  get validRowCount(): number {
    return this.submittableRows.filter(row => row.validityStatus === 'valid')
      .length
  }

  /**
   * Populated rows the user still has to fix. RETIRED rows can never contribute,
   * however they acquired the flag.
   *
   * That exclusion closes the retired-AND-invalid deadlock BY CONSTRUCTION rather
   * than by trusting every write path to remember the rule. A row that is both
   * retired and `invalid` is excluded from `submittableRows` yet counted here, so
   * `submitDisabled` would be true forever for the WHOLE grid while
   * `revalidateRow` early-returns and `editable` is false — unclearable except by
   * delete/Clear, both of which destroy the record of a possibly-queued command.
   * `applyRowResults` and `markRowsUnconfirmed` are each careful not to create
   * that state, but they are not the only way in: a result the client cannot
   * attribute leaves the row `invalid` and the subsequent UNCONFIRMED pass then
   * retires it, and any future write path would inherit the same trap. Deriving
   * the count from `!isRowRetired(row)` makes the deadlock unreachable instead of
   * merely unproduced.
   */
  get invalidRowCount(): number {
    return this.activeRows.filter(
      row => !isRowRetired(row) && row.validityStatus === 'invalid',
    ).length
  }

  get submitDisabled(): boolean {
    return (
      this.submitting ||
      !this.activeEntry ||
      this.activeRows.length === 0 ||
      this.submittableRows.length === 0 ||
      this.invalidRowCount > 0
    )
  }

  /**
   * True while a batch POST is in flight: the whole grid is frozen.
   *
   * `submitting` alone used to feed only `submitDisabled`, which left every OTHER
   * mutation open between the POST and its response — and each one reopens the
   * duplicate-submission defect this tab spends most of its logic closing:
   *
   * - Clear / a command-type switch / a per-row delete replaces or drops
   *   `rowData`, so when the response lands `findRow()` misses every submitted
   *   rowId, `applyRowResults` merely increments `unmappedResultCount` and
   *   `markRowsUnconfirmed()` marks nothing. The grid then holds NO record that
   *   those commands were queued and presents a fresh, fully-submittable grid the
   *   user will re-enter and re-POST. That directly violates the tab's most
   *   important invariant: every transmitted row must end up carrying a status.
   * - Editing a cell of a transmitted row runs `revalidateRow` (the row is not
   *   retired until the response lands) and clears its status, after which the
   *   response stamps it `ACCEPTED` — so the row shows edited values that were
   *   never sent next to "Submitted — queued (cmdId)".
   *
   * Bound on the toolbar controls in the template AND enforced as an early
   * return in `addRow`/`clearAll`/`deleteRow`/`onCommandTypeChange`/`onPaste`,
   * because the per-row delete is an ungated `onCellClicked` handler with no
   * `[disabled]` to bind. The `editable` colDef callbacks consult it too.
   *
   * Every transition of this flag goes through `setSubmitting`, which redraws the
   * rows — see that method for why an `editable` callback alone does not reach
   * the checkbox columns.
   */
  get gridLocked(): boolean {
    return this.submitting
  }

  /**
   * The ONLY writer of `submitting` — and therefore of `gridLocked` — because
   * both directions of that transition have to be pushed into cells that are
   * already rendered.
   *
   * The text columns re-read `editable` when ag-grid starts an edit, so they are
   * safe either way. `EditableCheckboxRenderer` is not: it CACHES the result of
   * `column.isCellEditable(node)` into a private `disabled` field inside
   * `setDisabled()`, which runs only from `init`/`refresh` and from the grid-wide
   * `EVENT_CELL_VALUE_CHANGED` listener it registers — and `applyClick()` then
   * gates on that cached flag rather than re-consulting the column. A
   * `gridLocked` transition that does not force a redraw is therefore invisible
   * to every checkbox cell on screen, in BOTH directions:
   *
   * - locking without a redraw leaves `disabled === false`, so a mid-flight click
   *   still reaches `node.setDataValue` -> `onCellValueChanged` ->
   *   `revalidateRow` on a transmitted-but-not-yet-retired row, and the response
   *   then stamps `ACCEPTED` beside a flag value that was never transmitted —
   *   exactly the defect `gridLocked` closes for the text columns;
   * - releasing without a redraw leaves `disabled === true` forever (the redraws
   *   in `applyRowResults`/`handleSubmitFailure` both run while `submitting` is
   *   still true), so after any submit the checkboxes are dead on every
   *   non-retired row — including the server-REJECTED rows of a partial batch,
   *   where ticking `ignoreCostBasisAdjustments` and resubmitting is the
   *   documented remedy those columns exist to provide (PVAL-25173).
   */
  private setSubmitting(submitting: boolean): void {
    this.submitting = submitting
    // The flag is written FIRST and the repaint is guarded, in that order and for
    // that reason: this method is called from `submit()`'s `finally`, so a throw
    // out of `redrawRows` here would latch `submitting` at `true` — Submit,
    // Clear, Add Row, the command-type select and every editable cell dead for
    // the rest of the page session, with no message, and no `ngOnInit` re-run to
    // reset it because the panel deliberately keeps this child alive.
    this.redrawRows()
  }

  /**
   * Redraw the rendered rows, tolerating a grid that cannot repaint.
   *
   * `redrawRows` is on the FAILURE paths as well as the happy path, and one of
   * the concrete failures those paths exist to survive is a grid torn down while
   * a batch was in flight — where the call itself throws. Letting that escape
   * would either latch the submit guard (see `setSubmitting`) or re-throw out of
   * the recovery that keeps the row states honest. A lost repaint is always the
   * cheaper failure: the row objects are already correct, and the next redraw or
   * data push refreshes the view.
   */
  private redrawRows(): void {
    try {
      this.gridApi?.api.redrawRows()
    } catch {
      // The grid is gone or refused the repaint; the row state stands regardless.
    }
  }

  /** Header status pill text. Never implies a command was booked. */
  get statusPillText(): string {
    if (this.activeRows.length === 0) {
      return 'No rows entered'
    }
    if (this.invalidRowCount > 0) {
      return `${this.invalidRowCount} of ${this.activeRows.length} rows invalid`
    }
    if (this.submittableRows.length === 0) {
      // Every populated row is retired. Report unconfirmed rows explicitly —
      // folding them into the queued count would claim an outcome the client
      // does not have.
      if (this.unconfirmedRowCount > 0) {
        return `${this.unconfirmedRowCount} of ${this.activeRows.length} rows unconfirmed`
      }
      return `${this.queuedRowCount} of ${this.activeRows.length} rows submitted — queued`
    }
    return `${this.validRowCount} of ${this.activeRows.length} rows ready to submit`
  }

  get statusPillClass(): string {
    if (this.activeRows.length === 0) {
      return 'badge badge-secondary'
    }
    if (this.invalidRowCount > 0) {
      return 'badge badge-danger'
    }
    // An unknown outcome is not a success — don't paint it green.
    return this.unconfirmedRowCount > 0
      ? 'badge badge-warning'
      : 'badge badge-success'
  }

  onCommandTypeChange(commandType: string): void {
    if (this.gridLocked) {
      return
    }
    this.setCommandType(commandType)
    // USER GESTURE, one of only two allowed to reset rows: the old rows belong
    // to the previous command type's columns, so they cannot carry over.
    this.resetRows()
  }

  /**
   * Rebuild the COLUMNS for a command type. Does NOT touch rows: callers pair
   * this with `resetRows()` (user gesture) or `initialiseRowsFor()` (machine
   * path). Do not put row handling back in here, it would blank the retained
   * grid on every rebuild (PVAL-26303 #2).
   */
  private setCommandType(commandType: string): void {
    this.selectedCommandType = commandType
    this.colDefs = this.buildColDefs()
    this.gridOptions = { ...this.gridOptions, columnDefs: this.colDefs }
    this.gridApi?.api.setColumnDefs(this.colDefs)
  }

  /**
   * The MACHINE path's row initialisation: restore the retained grid for
   * `commandType` minus any row that already became a command, or seed a blank
   * starter row (PVAL-26303 #2). Runs on every component construction.
   *
   * The filter runs on RESTORE, not on write, so the store keeps holding the
   * live array by reference and in-place cell edits stay captured.
   *
   * Both branches retain: the seed because `pushRowData` fires only on row-SET
   * mutations, the restore because filtering makes a new array.
   */
  private initialiseRowsFor(commandType: string): void {
    const retained = this.processingService.peekRetainedGridState(commandType)
    if (retained) {
      this.cellLevelErrorMessageMap = retained.cellLevelErrorMessageMap
      this.rowData = []
      retained.rows.forEach(row => {
        if (isRowQueued(row)) {
          this.clearRowErrors(row.rowId)
        } else {
          this.rowData.push(row)
        }
      })
      if (this.rowData.length === 0) {
        this.rowData.push(this.createRow())
      }
      // A submit still in flight when the old component died. Nobody will ever
      // learn whether it landed, so retire these UNCONFIRMED, the same
      // direction `handleSubmitFailure` takes for a response that never came.
      this.inFlightRowIds = retained.submittedRowIds
      if (this.inFlightRowIds.size > 0) {
        this.markRowsUnconfirmed(this.inFlightRowIds)
        this.inFlightRowIds.clear()
      }
      this.pushRowData()
    } else {
      this.clearGridMessages()
      this.rowData = [this.createRow()]
      this.pushRowData()
    }
  }

  /**
   * Reset the grid to a single blank row and retain that.
   *
   * USER GESTURES ONLY (`onCommandTypeChange`, `clearAll`). On a load path this
   * blanks the user's retained rows; use `initialiseRowsFor` there.
   */
  private resetRows(): void {
    this.clearGridMessages()
    this.rowData = [this.createRow()]
    this.pushRowData()
  }

  /**
   * Drop every grid-level (as opposed to per-row) message. Batch-level notices
   * are deliberately not retained: a restored footer summary would describe an
   * earlier submit as though it had just happened.
   */
  private clearGridMessages(): void {
    this.cellLevelErrorMessageMap.clear()
    this.unmappedResultCount = 0
    this.lastSubmissionSummary = ''
    this.submitError = ''
  }

  /**
   * Keep `[data]` and the grid in step after any row-set mutation, and retain
   * the new row set. Every such mutation must retain, because the re-spread
   * below replaces the array the store points at. In-place CELL edits need no
   * write: they mutate the row objects it already holds.
   */
  private pushRowData(): void {
    this.rowData = [...this.rowData]
    this.gridApi?.api.setRowData(this.rowData)
    this.processingService.retainGridState({
      commandType: this.selectedCommandType,
      rows: this.rowData,
      cellLevelErrorMessageMap: this.cellLevelErrorMessageMap,
      submittedRowIds: this.inFlightRowIds,
    })
  }

  private createRow(values: ProcessingRowValues = {}): ProcessingGridRow {
    this.rowSequence += 1
    const row: ProcessingGridRow = {
      // Client-generated, stable for the life of the row, and used verbatim as
      // ag-grid's row node id so submitted rowId and grid row id can never drift.
      rowId: `ca-proc-${this.instanceId}-${this.rowSequence}`,
      accountId: '',
      exDate: '',
      targetCusip: '',
      cashPerShare: '',
      cashReceived: '',
      destCusip: '',
      destShareExchRate: '',
      destCusipMarketPrice: '',
      destCusip2: '',
      destShareExchRate2: '',
      destCusipMarketPrice2: '',
      notes: '',
      validityStatus: 'inactive',
      errorMessageMap: new Map<string, string[]>(),
      // Checkbox flags default to the backend's declared `Boolean = false`.
      // NOT routed through PROCESSING_VALIDATED_FIELDS: a `false` there would be
      // a non-blank value, so `isRowEmpty` would stop recognising a fresh
      // untouched row and `activeRows` / the starter-row replacement in
      // `appendRows` would both break.
      ignoreCostBasisAdjustments: false,
      forceRealizeAll: false,
    }
    PROCESSING_VALIDATED_FIELDS.forEach(field => {
      const value = values[field]
      if (value !== undefined) {
        row[field] = value
      }
    })
    return row
  }

  addRow(): void {
    if (this.gridLocked) {
      return
    }
    this.rowData.push(this.createRow())
    this.pushRowData()
  }

  clearAll(): void {
    if (this.gridLocked) {
      return
    }
    this.resetRows()
  }

  deleteRow(rowId: string): void {
    if (this.gridLocked) {
      return
    }
    this.rowData = this.rowData.filter(row => row.rowId !== rowId)
    this.clearRowErrors(rowId)
    if (this.rowData.length === 0) {
      this.rowData.push(this.createRow())
    }
    this.pushRowData()
  }

  /**
   * Copy the active command type's column headers, in paste order, to the
   * clipboard.
   *
   * The write is AWAITED and its rejection handled, because
   * `navigator.clipboard.writeText` is a permissioned API that genuinely fails:
   * a non-secure context, a denied permission or an unfocused document all
   * reject (and `navigator.clipboard` is `undefined` outright in an insecure
   * context, which throws synchronously). Floating the promise produced an
   * unhandled rejection AND a "Copied to Clipboard" toast for a clipboard that
   * was never written — the user then pastes stale content into Excel and maps
   * their batch against the wrong column order, which is the one thing this
   * button exists to prevent.
   */
  async copyHeaders(): Promise<void> {
    if (!this.activeEntry) {
      return
    }
    try {
      await navigator.clipboard.writeText(buildCopyHeaders(this.columns))
    } catch {
      this.toastService.error(COPY_HEADERS_ERROR)
      return
    }
    this.toastService.success('Copied to Clipboard')
  }

  /**
   * Excel paste: split on newlines/tabs, map positionally to the active
   * registry entry's column set, and APPEND as new rows.
   *
   * The handler is bound on the tab's root container, and paste events BUBBLE —
   * so a paste aimed at an open inline cell editor (rendered in-cell, since
   * `disablePopover: true`) or at the toolbar's own controls reaches here too.
   * Two guards keep this path off the editor's turf, because
   * `preventDefault()` here would both swallow the value the user meant to put
   * in the cell AND append a junk row:
   *
   * 1. the event target owning its own paste behaviour — a text `<input>` (an
   *    open in-cell editor), a `<textarea>` or a contenteditable host — and the
   *    grid reporting an open editor via
   *    `getEditingCells()`, the same signal the repo's bulk panels gate their
   *    own key handling on (cf. `bulk-cash-entry.component.ts:760`). Note a
   *    `<select>` and a checkbox `<input>` are deliberately NOT such targets:
   *    neither can receive pasted text, and matching them here silently
   *    swallowed the paste (see `isEditableEventTarget`);
   * 2. clipboard text that is not actually a row block. A scalar with no tab or
   *    newline is the most common clipboard action in a spreadsheet grid and
   *    belongs to the cell editor; multi-line single-column pastes still append.
   */
  onPaste(event: ClipboardEvent): void {
    if (!this.activeEntry) {
      return
    }
    if (this.gridLocked) {
      return
    }
    if (isEditableEventTarget(event.target)) {
      return
    }
    if ((this.gridApi?.api.getEditingCells()?.length ?? 0) > 0) {
      return
    }
    const clipboardText = stripTrailingNewline(
      event.clipboardData?.getData('text/plain') ?? '',
    )
    if (!ROW_BLOCK_PATTERN.test(clipboardText)) {
      return
    }
    const parsedRows = parsePastedProcessingRows(clipboardText, this.columns)
    if (parsedRows.length === 0) {
      return
    }
    event.preventDefault()
    this.appendRows(parsedRows)
  }

  /** @internal exposed for testing the paste path without a ClipboardEvent. */
  appendRows(parsedRows: ProcessingRowValues[]): void {
    // A single untouched starter row is replaced rather than left stranded above
    // the pasted block.
    if (this.rowData.length === 1 && isRowEmpty(this.rowData[0])) {
      this.clearRowErrors(this.rowData[0].rowId)
      this.rowData = []
    }
    parsedRows.forEach(values => {
      const row = this.createRow(values)
      this.revalidateRow(row)
      this.rowData.push(row)
    })
    this.pushRowData()
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      getRowNodeId: ({ rowId }: ProcessingGridRow) => rowId,
      // ag-grid's default is 2000ms, which reads as "no tooltip". The sibling
      // bulk-entry grids in `libs/create-transactions-modal` all set 0.
      tooltipShowDelay: 0,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        // REQUIRED for a tooltip here to render at all. Northfork's
        // `GridTooltip` falls back to `cellHasTextOverflow` without a
        // `showTooltip`, so a tooltip string alone is suppressed unless the text
        // overflows. `heliosClientSideRowConfig` carries the same entry, which
        // this grid does not spread. Reaches the flag headers and the delete
        // column; the other columns set their own params.
        tooltipComponentParams: { showTooltip: () => true },
      },
      sideBar: false,
      // No `rowSelection`: this grid has no checkbox-selection column and
      // nothing reads a selection, and the agreed spec is explicitly "no
      // multi-select, no delete-confirmation modal" — a per-row delete icon
      // instead. Setting it would enable row click-selection highlighting that
      // means nothing here.
      //
      // Range selection needs both the gridOptions flag AND the per-grid
      // RangeSelectionModule ([modules] on nf-table-layout); without the module
      // `getCellRanges()` silently returns nothing and copy is a no-op.
      suppressMultiRangeSelection: false,
      enableRangeSelection: true,
      enableCellChangeFlash: true,
      columnDefs: this.colDefs,
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      onCellValueChanged: (event: CellValueChangedEvent) =>
        this.onCellValueChanged(event),
      onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
      // There is deliberately NO `onFirstDataRendered: sizeColumnsToFit()`, and
      // the absence is load-bearing — do not copy one back in from a sibling
      // bulk grid.
      //
      // `sizeColumnsToFit` rescales EVERY column to the viewport width, which
      // discards the per-column widths the registry declares (100-220px across
      // the 12 data columns, ~2176px in total with the validity/#/flag/Submission
      // /delete columns) and scales them toward `defaultColDef.minWidth`. In a
      // GoldenLayout panel narrower than that total — the normal case, since
      // Helios users keep panels side by side, so this panel is routinely half a
      // screen — everything shrank proportionally: Ex Date landed near 45-60px,
      // which truncates the `YYYY-MM-DD` value the operator has to read and type.
      // Reporter-confirmed on the MR against a real half-screen panel ("it looks
      // fairly cramped"). And because `[autoResizeGrid]="false"` this ran once on
      // first data render, so widening the panel afterwards left the columns
      // narrow with dead space rather than re-fitting.
      //
      // Without it the declared widths simply stand and ag-grid provides its own
      // horizontal scroll, which is the legible behaviour for a 17-column
      // spreadsheet-style grid. Columns remain user-resizable either way.
    }
  }

  /** @internal */
  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event
    enableHeliosCopyBehavior(event.api)
    event.api.setColumnDefs(this.colDefs)
    event.api.setRowData(this.rowData)
  }

  private buildColDefs(): ColDef[] {
    return [
      {
        colId: VALIDITY_STATUS_COL_ID,
        field: VALIDITY_STATUS_COL_ID,
        headerName: '',
        editable: false,
        sortable: false,
        suppressMovable: true,
        valueGetter: () => '',
        cellClass: (params: CellClassParams) =>
          deduceValidityStatusCellClass(params.data?.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponent: ProcessingErrorTooltip,
        tooltipComponentParams: {
          showTooltip: (params?: ITooltipParams) =>
            (params?.data?.errorMessageMap?.size ?? 0) > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
      },
      {
        colId: ROW_NUMBER_COL_ID,
        headerName: '#',
        editable: false,
        sortable: false,
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
      },
      ...this.columns.map(column => this.buildEditableColDef(column)),
      ...this.flagColumns.map(column => this.buildFlagColDef(column)),
      {
        colId: SUBMISSION_STATUS_COL_ID,
        headerName: 'Submission',
        editable: false,
        sortable: false,
        width: 200,
        valueGetter: (params: ValueGetterParams) =>
          this.describeSubmissionStatus(params.data),
        // `submissionMessages` is `result.messages` straight off the batch
        // response — the same untrusted, open-typed field for which this branch
        // replaced `innerHTML` with `textContent` in `ProcessingErrorTooltip`.
        //
        // A `tooltipValueGetter` with NO `tooltipComponent` renders through
        // ag-grid's DEFAULT tooltip component, and this grid does not fall back
        // to browser-native tooltips either: `createGridOptions()` spreads only
        // `heliosSizingAdjustments`, so it never inherits
        // `enableBrowserTooltips: true` from `heliosClientSideRowConfig` /
        // `heliosServerSideRowConfig`. Angular's sanitizer does not reach inside
        // an ag-grid tooltip, so this column has to be pinned to the hardened,
        // textContent-only tooltip explicitly. `messageMap` redirects it at
        // `submissionMessages` instead of `errorMessageMap`.
        tooltipValueGetter: (params: ITooltipParams) =>
          (params.data?.submissionMessages ?? []).join(' | '),
        tooltipComponent: ProcessingErrorTooltip,
        tooltipComponentParams: {
          showTooltip: (params?: ITooltipParams) =>
            (params?.data?.submissionMessages ?? []).length > 0,
          messageMap: (params?: ITooltipParams) =>
            buildSubmissionMessageMap(params?.node?.data ?? params?.data),
        } as NfTooltipParams & ProcessingTooltipParams,
      },
      {
        colId: DELETE_COL_ID,
        headerName: '',
        editable: false,
        sortable: false,
        suppressMovable: true,
        width: heliosDefaultColWidth.SmallIcon,
        cellRenderer: () =>
          generateMdiIcon('mdi-close-circle-outline', ['text-danger']),
        headerTooltip: 'Delete row',
        tooltipValueGetter: () => 'Delete row',
      },
    ]
  }

  private buildEditableColDef(column: ProcessingColumn): ColDef {
    return {
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      // A legible floor, overriding the `defaultColDef.minWidth` these columns
      // would otherwise inherit — that constant is
      // `heliosDefaultColWidth.ValidityStatusCol`, the width of the validity
      // STATUS ICON, and an icon width is not a sane minimum for a cell the
      // operator has to read and type a date or a CUSIP into. Set below every
      // width the registry declares (the narrowest is 100px), so it never changes
      // a column's rendered width — it only stops a manual drag collapsing a data
      // column to an unreadable sliver.
      minWidth: PROCESSING_DATA_COL_MIN_WIDTH,
      sortable: false,
      type: getInlineEditColumnType(),
      // A retired row is locked. Without this, a user could edit a queued (or
      // unknown-outcome) row — which would clear the evidence it was sent and
      // make it eligible for a second POST of the same corporate action.
      //
      // `gridLocked` closes the same hole for the in-flight window: a row that
      // has been transmitted is not retired until the response lands, so without
      // it an edit would run `revalidateRow`, clear the status, and leave the row
      // showing values that were never sent under whatever status the response
      // then stamps on it.
      editable: (params: EditableCallbackParams) =>
        !isRowRetired(params.data) && !this.gridLocked,
      cellRendererParams: {
        editButtonClickOnly: true,
      } as InlineEditDisplayOptions,
      // `disablePopover` forces the editor to render in-cell rather than as a
      // floating overlay, which is fine for a text/number `<input>` but not for
      // the Date type's calendar widget: no other date column in this codebase
      // (bulk-cash-entry, trade-entry, wire-transfer, etc.) sets it, and forcing
      // it here left the picker with no usable space to render in a ~60px-wide
      // cell — it opened and was immediately clipped by the grid's scroll
      // viewport, with no working fallback to type the date manually either.
      cellEditorParams:
        column.dataType === 'date'
          ? { dataType: toInlineEditDataType(column.dataType) }
          : {
              dataType: toInlineEditDataType(column.dataType),
              disablePopover: true,
            },
      cellStyle: (params: CellClassParams) => this.getCellStyle(params),
      tooltipValueGetter: (params: ITooltipParams) =>
        this.getCellTooltip(params),
      tooltipComponentParams: {
        showTooltip: (params?: ITooltipParams) => this.showCellTooltip(params),
      } as NfTooltipParams,
    }
  }

  /**
   * Checkbox colDef for a boolean flag.
   *
   * Uses `EditableCheckboxRenderer` from `@helios/ag-grid-config` — the same
   * renderer the bulk-entry grids use — rather than the inline-edit column type:
   * `InlineEditDataType` has no `Boolean` member, so there is nothing for
   * `toInlineEditDataType` to map to. The renderer writes a real `boolean` via
   * `node.setDataValue`, which is why these fields are typed as booleans on
   * `ProcessingGridRow` and are NOT part of `ProcessingRowField`.
   *
   * The `editable` callback is the same
   * `!isRowRetired(params.data) && !this.gridLocked` predicate the text columns
   * use, and the renderer's own `isEditable` consults
   * `params.column.isCellEditable(node)` — but only from `init`/`refresh`, and it
   * CACHES the answer in a private `disabled` field that `applyClick` gates on.
   * So the callback alone is not enough: a status or `gridLocked` change only
   * reaches a rendered checkbox when the rows are redrawn. `applyRowResults` /
   * `handleSubmitFailure` cover the retirement transition and `setSubmitting`
   * covers the lock/release transition; do not drop either redraw.
   *
   * `headerTooltip` falls back to `headerName`, which just repeats the label, so
   * a flag with a consequence worth stating should declare its own
   * (PVAL-26303 #3).
   */
  private buildFlagColDef(column: ProcessingFlagColumn): ColDef {
    return {
      colId: column.field,
      field: column.field,
      headerName: column.headerName,
      sortable: false,
      width: 180,
      cellRenderer: EditableCheckboxRenderer,
      editable: (params: EditableCallbackParams) =>
        !isRowRetired(params.data) && !this.gridLocked,
      headerTooltip: column.headerTooltip ?? column.headerName,
    }
  }

  private cellKey(rowId: string, field: string): string {
    return `${rowId}-${field}`
  }

  /** @internal */
  getCellStyle(params: CellClassParams): CellStyle {
    const field = params.colDef.field ?? ''
    const rowId = params.data?.rowId ?? ''
    return this.cellLevelErrorMessageMap.has(this.cellKey(rowId, field))
      ? { 'background-color': 'lightcoral' }
      : { 'background-color': '#00000000' }
  }

  /** @internal */
  getCellTooltip(params: ITooltipParams): string {
    const field = (params.colDef as ColDef)?.field ?? ''
    const rowId = params.data?.rowId ?? ''
    return (
      this.cellLevelErrorMessageMap.get(this.cellKey(rowId, field)) ?? []
    ).join(' | ')
  }

  /** @internal */
  showCellTooltip(params?: ITooltipParams): boolean {
    return this.getCellTooltip(params ?? ({} as ITooltipParams)).length > 0
  }

  /** @internal */
  onCellValueChanged(event: CellValueChangedEvent): void {
    const row = event.node.data as ProcessingGridRow
    this.revalidateRow(row)
    this.gridApi?.api.redrawRows()
  }

  /** @internal */
  onCellClicked(event: CellClickedEvent): void {
    if (event.column.getColId() === DELETE_COL_ID) {
      this.deleteRow((event.data as ProcessingGridRow).rowId)
    }
  }

  private clearRowErrors(rowId: string): void {
    PROCESSING_VALIDATED_FIELDS.forEach(field =>
      this.cellLevelErrorMessageMap.delete(this.cellKey(rowId, field)),
    )
  }

  /**
   * Live per-cell/per-row validation. Editing a row also drops any submission
   * status it carried, so a stale "Submitted" label can never sit next to
   * changed values.
   */
  private revalidateRow(row: ProcessingGridRow): void {
    if (isRowRetired(row)) {
      // A retired row (queued, or sent with an unknown outcome) is never
      // re-validated, and its submission status and commandId are never erased.
      // That id is the only record of the queued command the UI holds, and
      // clearing either would also make the row look submittable again.
      return
    }
    this.clearRowErrors(row.rowId)
    row.submissionStatus = undefined
    row.submissionMessages = undefined
    row.commandId = undefined

    if (isRowEmpty(row)) {
      row.validityStatus = 'inactive'
      row.errorMessageMap = new Map<string, string[]>()
      return
    }

    const errors = validateProcessingRow(row)
    const errorMap = toFieldErrorMap(errors)
    row.errorMessageMap = errorMap
    row.validityStatus = errors.length === 0 ? 'valid' : 'invalid'
    errorMap.forEach((messages, field) =>
      this.cellLevelErrorMessageMap.set(
        this.cellKey(row.rowId, field),
        messages,
      ),
    )
  }

  /**
   * Row/footer wording for a submission outcome.
   *
   * `ACCEPTED` reads as "Submitted — queued", never "Booked" or "Success": it
   * only means the HeliosCommand was queued. perseus-worker's cash
   * reconciliation, security eligibility, open lots and cost-basis checks run
   * later and surface via the job-failure toast + Jobs panel.
   *
   * An UNRECOGNISED status is reported as unconfirmed rather than passed through
   * bare. The row has been retired (fail closed, see `isRowRetired`), so the label
   * has to match what the rest of the UI is doing with it: a bare `QUEUED` next to
   * a locked, un-resubmittable row reads like a bug, and worse, reads like
   * something the user could fix in the grid. The raw status is kept in the text
   * so the server's own vocabulary is still visible.
   */
  describeSubmissionStatus(row?: ProcessingGridRow): string {
    if (!row?.submissionStatus) {
      return ''
    }
    if (row.submissionStatus === ROW_STATUS_ACCEPTED) {
      return row.commandId
        ? `Submitted — queued (${row.commandId})`
        : 'Submitted — queued'
    }
    if (row.submissionStatus === ROW_STATUS_UNCONFIRMED) {
      // Neither "queued" nor "rejected" — the client genuinely does not know.
      return 'Unconfirmed — check Commands drawer'
    }
    if (isRowRetired(row)) {
      return `${row.submissionStatus} — unconfirmed, check Commands drawer`
    }
    return row.submissionStatus
  }

  async submit(): Promise<void> {
    const entry = this.activeEntry
    if (this.submitDisabled || !entry) {
      return
    }
    // Claim the submit slot HERE — synchronously, before the first `await`.
    //
    // `submitDisabled` is the only guard against a second entry into this
    // method, and it reads `this.submitting`. Setting the flag after
    // `await modalRef.result` (as this originally did) left a window in which
    // two submit() calls entered before the modal resolved BOTH passed the
    // guard above, both captured the same `rows` snapshot and both POSTed the
    // same rowIds — duplicating a financial corporate-action command that
    // nothing catches synchronously, which is the exact defect class the
    // retirement logic elsewhere in this file exists to close. The NgbModal
    // backdrop only incidentally mitigates a fast double-click; it is not an
    // intentional guard.
    //
    // Consequence: EVERY early return below must clear the flag again, or
    // Submit stays disabled for the rest of the session. The POST path clears
    // it in its `finally`. Both directions go through `setSubmitting` so the
    // lock actually reaches the already-rendered checkbox cells.
    this.setSubmitting(true)

    // Only rows that have NOT been retired are sent. On a retry after a partial
    // batch this is what keeps previously-ACCEPTED rows — and rows whose outcome
    // was never confirmed — out of the second POST.
    const rows = this.submittableRows

    // The modal is opened INSIDE the guard, not just awaited inside one.
    //
    // `modalService.open()` and the two `componentInstance` writes used to sit
    // outside any try/catch, with only `modalRef.result` caught. If `open()`
    // throws — or hands back a ref whose `componentInstance` is undefined —
    // `submit()` rejects with `submitting` still `true`, and that flag feeds BOTH
    // `submitDisabled` and `gridLocked`: Submit, Clear, Add Row, the command-type
    // select and every editable cell go dead for the rest of the page session
    // with no message at all, and the panel's keep-alive latch means a tab round
    // trip cannot re-run `ngOnInit` to reset it. That is exactly the "Submit stays
    // dead for the session" hazard this method's own docblock names, on the one
    // path it did not cover.
    //
    // A broken modal and a dismissed modal are treated identically from here:
    // nothing was confirmed, so nothing is sent and the guard is released.
    let confirmed: unknown = false
    try {
      const modalRef = this.modalService.open(
        ProcessingSubmitConfirmationModal,
        { keyboard: false },
      )
      modalRef.componentInstance.rowCount = rows.length
      modalRef.componentInstance.commandLabel = entry.label
      confirmed = await modalRef.result
    } catch {
      confirmed = false
    }
    if (!confirmed) {
      this.setSubmitting(false)
      return
    }

    this.submitError = ''
    this.lastSubmissionSummary = ''

    // The rowIds actually transmitted. EVERY one of them must end up carrying a
    // status — either mapped from a result or marked UNCONFIRMED. A transmitted
    // row left unmarked keeps `submissionStatus === undefined` and stays in
    // `submittableRows`, so the next Submit silently re-sends it.
    const submittedRowIds = new Set(rows.map(row => row.rowId))

    try {
      // The batch endpoint takes `helios-session-id` as a required non-nullable
      // header, which the session interceptor only supplies while a session is
      // active — so a blank session is created/activated first. Awaited in its
      // OWN try/catch so a session failure (nothing ever left the browser, safe
      // to retry as-is) is never conflated with a post-transmission failure
      // (outcome genuinely unknown).
      await this.blankSessionService.createBlankSessionIfNotInSession()
    } catch {
      this.setSubmitting(false)
      this.submitError = SESSION_ERROR
      this.toastService.error(SESSION_ERROR)
      return
    }

    // The wire body is built BEFORE the transmission try, on purpose.
    //
    // A coercion throw here means nothing ever left the browser — the same
    // semantics as a failed session, and the OPPOSITE of what
    // `handleSubmitFailure` assumes. Inside the POST's catch it would have been
    // classified as a post-transmission failure (`isDefiniteServerRejection` is
    // false for anything that is not an `HttpErrorResponse`) and retired a batch
    // that was never sent.
    let request: CorporateActionsProcessingBatchRequest
    try {
      request = buildProcessingBatchRequest(rows, entry.commandType)
    } catch {
      this.setSubmitting(false)
      this.submitError = SUBMIT_NOT_SENT_ERROR
      this.toastService.error(SUBMIT_NOT_SENT_ERROR)
      return
    }

    // ONE `finally` owns the release from here on, and BOTH handlers run inside
    // it. The two inner `try`s only choose the handler; neither may own the
    // release, because a handler that throws would then skip it.
    //
    // The transmission catch used to release the flag with a bare statement
    // AFTER calling `handleSubmitFailure`, and that handler is not
    // throw-free: `markRowsUnconfirmed` -> `findRow` reaches into the grid and
    // `toastService.error` is an injected collaborator. This file's own premise
    // is that such a throw is REACHABLE — `redrawRows()` is wrapped precisely
    // because "a grid torn down while a batch was in flight" makes the call
    // itself throw, and the POST is `toPromise()`'d with no
    // `takeUntil(destroyed$)`, so the response can land after the view is gone.
    // Skipping the release there latches `submitting`, which feeds BOTH
    // `submitDisabled` and `gridLocked`: Submit, Clear, Add Row, the
    // command-type select and every editable cell go dead for the rest of the
    // page session with no message, and the panel's `[destroyOnHide]`
    // keep-alive latch means no `ngOnInit` re-run can reset it. That is the
    // same hazard the modal path was fixed for; the apply branch below already
    // used a `finally`, so this is the last statement pair that could escape.
    // Claimed after the modal and the request build, so a cancelled or
    // never-sent batch leaves nothing behind, and released in the `finally`. If
    // the component dies first, the manifest survives in the store and the
    // restore retires these rows instead of handing them back submittable.
    submittedRowIds.forEach(rowId => this.inFlightRowIds.add(rowId))

    let response: CorporateActionsProcessingBatchResponse | undefined
    try {
      // This inner try wraps TRANSMISSION ONLY, and that scoping is load-bearing.
      //
      // `applyRowResults` used to run inside it. Because
      // `isDefiniteServerRejection` returns false for anything that is not an
      // `HttpErrorResponse`, ANY throw while applying a SUCCESSFUL 200 — a
      // `redrawRows` against a torn-down grid, the toast service, an ag-grid
      // internal — was routed to `handleSubmitFailure`. On a response that did
      // arrive and was partly applied that is actively harmful: it zeroes the
      // `unmappedResultCount` just computed, tells the user "no response came
      // back" about a batch that came back with per-row results while
      // `lastSubmissionSummary` still shows those results, and — worst —
      // `markRowsUnconfirmed` retires every not-yet-retired submitted row
      // INCLUDING the ones `applyRowResults` had just marked a PROVEN
      // rejection. Those are the rows this code deliberately leaves editable
      // and fixable, and retiring them locks them permanently: `revalidateRow`
      // early-returns, both `editable` callbacks are false, and the only escape
      // is delete/Clear, which destroy the record of a possibly-queued command.
      try {
        response = await this.processingService
          .submitBatch(request)
          .pipe(take(1))
          .toPromise()
      } catch (error) {
        this.handleSubmitFailure(error, submittedRowIds)
        return
      }

      try {
        this.applyRowResults(response?.results ?? [], submittedRowIds)
      } catch {
        this.handleApplyResultsFailure(submittedRowIds)
      }
    } finally {
      // Cleared in place, not reassigned: the store holds this exact Set.
      this.inFlightRowIds.clear()
      // Releases the grid AND redraws: the row-level redraws above both ran
      // while `submitting` was still true, so without this every checkbox cell
      // would stay disabled on rows the user still has to act on. `return`s
      // inside the try above run this too, which is the point.
      this.setSubmitting(false)
    }
  }

  /**
   * Classify a failed POST — the client must not assert an outcome it cannot
   * observe.
   *
   * A definite server rejection (4xx other than 408) means the batch was never
   * processed, so the rows are left untouched and stay submittable. That matters
   * because the contract's whole-batch Jackson rejection IS a 400: retiring the
   * user's rows on that path would destroy a batch we know was never queued.
   *
   * Anything else — status 0 (no response arrived at all: network drop, CORS,
   * abort), 408, or any 5xx including a 504 gateway timeout — may have been
   * queued server-side before the response was lost. Those rows are retired as
   * UNCONFIRMED so a following Submit cannot silently duplicate them.
   */
  private handleSubmitFailure(
    error: unknown,
    submittedRowIds: Set<string>,
  ): void {
    // Reset the unattributable-result counter for the same reason
    // `applyRowResults` does it at the top of its own run: it describes ONE
    // batch. Without this a stale "N result(s) could not be matched to a row"
    // from an earlier partial batch stayed in the footer next to this batch's
    // failure message — this response carried no results at all, so the only
    // honest value is 0.
    this.unmappedResultCount = 0
    if (isDefiniteServerRejection(error)) {
      this.submitError = SUBMIT_REJECTED_ERROR
      this.toastService.error(SUBMIT_REJECTED_ERROR)
      return
    }
    this.markRowsUnconfirmed(submittedRowIds)
    this.submitError = SUBMIT_UNCONFIRMED_ERROR
    this.toastService.error(SUBMIT_UNCONFIRMED_ERROR)
    this.redrawRows()
  }

  /**
   * Recover from a throw while APPLYING a response that DID arrive.
   *
   * Deliberately NOT `handleSubmitFailure`, for two independent reasons:
   *
   * 1. its copy asserts that no response came back, which on this path is false —
   *    the POST succeeded and per-row results were being written onto the grid;
   * 2. `markRowsUnconfirmed(submittedRowIds)` would sweep up every submitted row
   *    that is not yet retired, INCLUDING the rows `applyRowResults` had already
   *    marked a PROVEN rejection. Those are the one outcome this tab deliberately
   *    leaves editable so the user can fix and resend them, and retiring them
   *    locks them for good.
   *
   * The tab's most important invariant still has to hold, though: every
   * transmitted row must end up carrying a status, or the next Submit re-POSTs it
   * and may duplicate a queued financial command. So exactly the rows the
   * interrupted pass never reached — those with no `submissionStatus` at all — are
   * retired UNCONFIRMED; rows the response already resolved keep what it said
   * about them. (`markRowsUnconfirmed` additionally skips anything already
   * retired, so a queued row cannot be disturbed either.)
   */
  private handleApplyResultsFailure(submittedRowIds: Set<string>): void {
    const unreportedRowIds = new Set(
      Array.from(submittedRowIds).filter(
        rowId => !this.findRow(rowId)?.submissionStatus,
      ),
    )
    this.markRowsUnconfirmed(unreportedRowIds)
    this.submitError = SUBMIT_RESULTS_ERROR
    this.toastService.error(SUBMIT_RESULTS_ERROR)
    this.redrawRows()
  }

  /**
   * Resolve a rowId to its grid row via `getRowNode` — which proves the grid row
   * id and the transmitted rowId are the same thing — falling back to the local
   * array whenever the grid cannot answer.
   *
   * The `getRowNode` call is wrapped for the same reason `redrawRows()` is, and
   * the reason is not hypothetical: the failures this method is called from
   * INSIDE are a torn-down grid or a mutated ag-grid row model, and neither
   * heals between statements. `handleApplyResultsFailure` opens by calling this
   * on every submitted rowId, and `markRowsUnconfirmed` calls it again on each
   * row it retires — so an unguarded throw here escaped the recovery that
   * exists to preserve the tab's most important invariant. Concretely: a
   * persistent `getRowNode` throw made `applyRowResults` throw before ANY row
   * was given a status, then threw AGAIN inside `handleApplyResultsFailure`,
   * out of `submit()`'s catch entirely — `submitError` stayed `''`, no toast
   * fired, `submit()` rejected unhandled out of a click handler, and every
   * transmitted row kept `submissionStatus === undefined` with
   * `validityStatus === 'valid'`, so it stayed in `submittableRows` and the
   * next Submit re-POSTed a batch that may already be queued.
   *
   * `this.rowData` is the component's own array and always answerable, so with
   * the fallback reachable on every path the recovery can always resolve its
   * rows. A grid that cannot answer costs only the `getRowNodeId` cross-check;
   * the row objects are the same objects either way.
   */
  private findRow(rowId: string): ProcessingGridRow | undefined {
    return (
      this.findRowNodeData(rowId) ??
      this.rowData.find(candidate => candidate.rowId === rowId)
    )
  }

  /** `getRowNode`'s row data, or `undefined` if the grid cannot answer. */
  private findRowNodeData(rowId: string): ProcessingGridRow | undefined {
    try {
      return this.gridApi?.api.getRowNode(rowId)?.data as
        | ProcessingGridRow
        | undefined
    } catch {
      // The grid is gone or its row model was mutated; the caller falls back to
      // `this.rowData`, which holds the same row objects.
      return undefined
    }
  }

  /**
   * Retire every transmitted row with no confirmed outcome, returning how many
   * were marked.
   *
   * `validityStatus` is deliberately NOT flipped to `invalid`: these rows were
   * valid when sent, and marking them invalid would inflate `invalidRowCount`
   * and disable Submit for unrelated rows the user still legitimately needs to
   * send. Retirement via `submissionStatus` is what keeps them out of the batch.
   */
  private markRowsUnconfirmed(rowIds: Set<string>): number {
    let marked = 0
    rowIds.forEach(rowId => {
      const row = this.findRow(rowId)
      if (!row || isRowRetired(row)) {
        return
      }
      row.submissionStatus = ROW_STATUS_UNCONFIRMED
      row.submissionMessages = [UNCONFIRMED_ROW_MESSAGE]
      // `commandId` is deliberately NOT cleared. A row reaching here cannot
      // carry one (a commandId retires the row, and retired rows are skipped
      // above), but "no path ever erases a commandId" is the invariant worth
      // stating in code rather than relying on that reasoning holding forever.
      this.clearRowErrors(row.rowId)
      marked += 1
    })
    return marked
  }

  /**
   * Footer summary.
   *
   * Counts are over the ROWS the client can account for, never over
   * `results.length` — deriving "rejected" as `results.length - accepted -
   * unmapped` let a response that omitted rows read "1 row(s) submitted and
   * queued, 0 rejected" after five rows had been sent.
   */
  private buildSubmissionSummary(
    accepted: number,
    rejected: number,
    unconfirmed: number,
  ): string {
    const parts = [
      `${accepted} row(s) submitted and queued`,
      `${rejected} rejected`,
    ]
    if (unconfirmed > 0) {
      parts.push(
        `${unconfirmed} row(s) unconfirmed — check the Commands drawer before resubmitting`,
      )
    }
    return `${parts.join(
      ', ',
    )}. Queued commands appear in the Commands drawer; deeper validations surface via the Jobs panel.`
  }

  /**
   * Map per-row results back onto the grid via `rowId` — the only correlation
   * key the contract provides — and then account for every row the response did
   * NOT mention.
   *
   * BOTH halves are scoped to `submittedRowIds`, the manifest of what this batch
   * actually transmitted. Mapping without that scope let a result for a rowId
   * that was never in the batch overwrite a live row's outcome; see the guard
   * inside the loop for why that reopens the duplicate-submission defect.
   *
   * The second half is load-bearing too. The response is not guaranteed to carry
   * a result per submitted row (a `200` with `results: []` is the degenerate
   * case), and a transmitted row left with `submissionStatus === undefined` and
   * `validityStatus === 'valid'` stays in `submittableRows`, keeps
   * `invalidRowCount` at 0, re-enables Submit and gets re-POSTed — duplicating a
   * financial corporate-action command that may already be queued.
   */
  private applyRowResults(
    results: CorporateActionProcessingRowResult[],
    submittedRowIds: Set<string>,
  ): void {
    this.unmappedResultCount = 0
    let accepted = 0
    let rejected = 0
    let retiredByServer = 0
    const resolvedRowIds = new Set<string>()

    // `results` is DECLARED as an array but arrives as parsed JSON, so it can be
    // absent, null or an object. Anything that is not an array is treated as "no
    // results at all", which drops every transmitted row through to the
    // UNCONFIRMED pass below. The alternative — letting `.forEach` throw — is
    // caught by `submit()` and classified as a post-transmission failure, and
    // `handleSubmitFailure` -> `markRowsUnconfirmed` would then retire rows this
    // method may already have flagged `invalid`: the retired-AND-invalid deadlock.
    const safeResults = Array.isArray(results) ? results : []

    safeResults.forEach(rawResult => {
      // Read as a `Partial` on purpose: the declared type promises `rowId` and
      // `status`, but this object came off the wire and the contract's own types
      // are open (`status: string`, `messages?: string[]`).
      const result = rawResult as
        | Partial<CorporateActionProcessingRowResult>
        | null
        | undefined

      // A MALFORMED result fails CLOSED — counted as unattributable so the row it
      // named (if any) drops through to the UNCONFIRMED pass instead of being
      // resolved here.
      //
      // A missing `status` key was a fail-OPEN hole, and the only one of its
      // family that was: `status: ''` already routes to this same safe outcome.
      // With `status` undefined the rowId was still added to `resolvedRowIds`, so
      // the account-for-every-transmitted-row pass skipped the row;
      // `isRowRetired` then returned false on its `submissionStatus === undefined`
      // branch, so the row fell into the PROVEN-rejection branch — flagged
      // `invalid`, `errorMessageMap` keyed by the literal `undefined`, and still
      // editable. One cell edit ran `revalidateRow`, cleared the status, and the
      // row was back in `submittableRows` for a second POST of a command that may
      // already be queued. That is exactly the "unrecognised status" case
      // `ROW_REJECTION_STATUSES`' docblock says must fail closed, entered through
      // an absent key rather than an unknown value.
      //
      // A `null` element or a non-string `rowId` is the same defect family
      // reached through a shape error rather than a value: unguarded it threw
      // inside this loop, and a successful `200` was then misclassified as a
      // post-transmission failure (see the `safeResults` note above).
      if (
        !result ||
        typeof result.rowId !== 'string' ||
        typeof result.status !== 'string' ||
        result.status.length === 0
      ) {
        this.unmappedResultCount += 1
        return
      }
      const resultRowId = result.rowId
      const resultStatus = result.status
      // `messages` and `commandId` are coerced ONCE, here, in the same block that
      // type-checks `rowId`/`status` — because they are the same untrusted,
      // open-typed wire fields and both reach code that assumes their declared
      // type.
      //
      // `messages` reaches `.join()` on the RENDER path (the Submission column's
      // `tooltipValueGetter`, and `ProcessingErrorTooltip.buildTable` via
      // `errorMessageMap`). A string `messages` — e.g.
      // `{"rowId":"...","status":"REJECTED","messages":"bad account"}` — threw
      // `TypeError: join is not a function` out of `redrawRows()` INSIDE this
      // method; `submit()`'s catch then saw a TypeError rather than an
      // `HttpErrorResponse`, `isDefiniteServerRejection` was false, and
      // `markRowsUnconfirmed` RETIRED rows the server had proven were rejected
      // and the user could have fixed — while `lastSubmissionSummary` and the
      // accepted toast never ran and the user was told "no response came back"
      // about a batch that came back with per-row results.
      //
      // `commandId` is coerced for the opposite reason: it must not silently flip
      // `isRowRetired`'s truthiness in the UNSAFE direction. Its presence is
      // positive proof a HeliosCommand exists, so `normaliseCommandId` preserves
      // every value that retired a row before and drops only the ones that never
      // did — in particular a `''`, which `?? row.commandId` would have written
      // straight over an existing commandId, erasing the UI's only record of a
      // queued command (the one thing no path may ever do).
      const resultMessages = Array.isArray(result.messages)
        ? result.messages.filter(
            (message): message is string => typeof message === 'string',
          )
        : []
      const resultCommandId = normaliseCommandId(result.commandId)
      // A result is only ever applied to a row that was in THIS batch.
      //
      // `findRow` resolves against the WHOLE grid, so without this guard a
      // result naming a rowId that was not transmitted lands on a live row. The
      // concrete case is a row ACCEPTED by an earlier partial batch, which stays
      // in the grid by design: the stray result overwrites its
      // `submissionStatus`, clears its `commandId` (the only record of the queued
      // command the UI holds) and — for any non-ACCEPTED status — flips
      // `validityStatus` to `invalid` and lifts the row OUT of `isRowRetired`,
      // putting it straight back into `submittableRows` so the next Submit
      // re-POSTs an already-queued financial command. Same shape for a blank or
      // untouched row the user never submitted at all, which would be pinned at
      // `invalid` and disable Submit with no way to clear it.
      //
      // `submittedRowIds` is the batch's own manifest, so it is the correct scope
      // for both halves of this method — the mapping above and the
      // account-for-every-transmitted-row pass below.
      if (!submittedRowIds.has(resultRowId)) {
        this.unmappedResultCount += 1
        return
      }
      const row = this.findRow(resultRowId)
      if (!row) {
        this.unmappedResultCount += 1
        return
      }
      // One result per rowId, and the FIRST one wins.
      //
      // The response is untrusted and open-typed, so a batch can name the same
      // rowId twice. Without this guard the second result overwrites the first
      // outcome unconditionally: for
      // `[{rowId:'r1',status:'ACCEPTED',commandId:'cmd-1'},{rowId:'r1',status:'REJECTED'}]`
      // the row ends up REJECTED with its `commandId` cleared, `validityStatus`
      // flipped to `invalid` and `isRowRetired` false — straight back into
      // `submittableRows` for a second POST of an already-queued financial
      // command, with the UI's only record of `cmd-1` gone. The footer counters
      // would double-count the row too. Same defect family as the out-of-batch
      // guard above, entered via a duplicate id rather than a stray one, so it
      // is accounted the same way: an unattributable result.
      if (resolvedRowIds.has(resultRowId)) {
        this.unmappedResultCount += 1
        return
      }
      resolvedRowIds.add(row.rowId)
      row.submissionStatus = resultStatus
      row.submissionMessages = resultMessages
      // `?? row.commandId`, never `?? undefined`: a commandId is the UI's only
      // record of a queued command, so no response may erase one. See
      // `normaliseCommandId` for why an off-contract value is normalised rather
      // than dropped.
      row.commandId = resultCommandId ?? row.commandId
      if (resultStatus === ROW_STATUS_ACCEPTED) {
        accepted += 1
      } else if (isRowRetired(row)) {
        // The incoming outcome RETIRES the row rather than rejecting it. Three
        // ways in, and they are handled identically on purpose:
        //
        // 1. a backend-returned literal `UNCONFIRMED`, which the model's docblock
        //    says must collapse to the same retired-and-flagged treatment as the
        //    client-side sentinel (the safe direction);
        // 2. a status this UI does not recognise as a PROVEN rejection — see
        //    `ROW_REJECTION_STATUSES`. This branch is the fail-CLOSED rule. The
        //    dangerous reading is "not ACCEPTED, therefore rejected": a
        //    `{status:'QUEUED', commandId:'cmd-7'}` row rendered red and stayed
        //    editable, the user edited a cell to "fix" it, `revalidateRow` dropped
        //    the status AND `cmd-7`, the row re-entered `submittableRows` and
        //    Submit re-POSTed an already-queued financial corporate action —
        //    which nothing catches synchronously, perseus-worker's checks being
        //    async;
        // 3. any status at all that arrived WITH a `commandId`. That id is
        //    positive proof the command exists; a rejection status beside one is
        //    contradictory server output, and the safe reading of a contradiction
        //    is "the command may exist".
        //
        // `validityStatus` is deliberately left alone here, exactly as
        // `markRowsUnconfirmed` does. Flagging a retired row `invalid` as well
        // is a deadlock: the row is excluded from `submittableRows` yet still
        // counted by `invalidRowCount`, so `submitDisabled` is true forever for
        // the WHOLE grid, while `revalidateRow` early-returns and `editable` is
        // false — the user cannot clear it by any means except delete/Clear,
        // both of which destroy the record of a possibly-queued command.
        retiredByServer += 1
      } else {
        // Only a PROVEN rejection (a `ROW_REJECTION_STATUSES` literal with no
        // `commandId`) lands here: nothing was queued, so the row stays editable
        // and, once fixed, submittable again.
        rejected += 1
        row.validityStatus = 'invalid'
        row.errorMessageMap = new Map<string, string[]>([
          [resultStatus, resultMessages],
        ])
      }
    })

    // `Array.from` rather than spreading the Set — the latter needs
    // `downlevelIteration` under a pre-ES2015 target.
    const unconfirmedRowIds = new Set(
      Array.from(submittedRowIds).filter(rowId => !resolvedRowIds.has(rowId)),
    )
    // A row the SERVER reported as retired (a literal `UNCONFIRMED`) is folded
    // in here rather than counted as rejected: the footer and the toast should
    // point it at the Commands drawer, which is the only surface that can answer
    // whether the command was queued.
    const unconfirmed =
      this.markRowsUnconfirmed(unconfirmedRowIds) + retiredByServer

    this.lastSubmissionSummary = this.buildSubmissionSummary(
      accepted,
      rejected,
      unconfirmed,
    )
    this.gridApi?.api.redrawRows()
    if (accepted > 0) {
      this.toastService.success(
        `${accepted} corporate action command(s) submitted and queued.`,
      )
    }
    if (unconfirmed > 0) {
      this.toastService.error(
        `${unconfirmed} row(s) could not be confirmed. Check the Commands drawer before resubmitting.`,
      )
    }
    // A 200 in which the server proved EVERY transmitted row rejected was the
    // one outcome with no inline message and no toast: both branches above are
    // zero, so the user's only signal was the footer summary. See
    // SUBMIT_ALL_REJECTED_ERROR. Safe to assert nothing was queued here — this
    // branch is only reachable when every row carried a declared rejection
    // status with no commandId.
    if (accepted === 0 && unconfirmed === 0 && rejected > 0) {
      this.submitError = SUBMIT_ALL_REJECTED_ERROR
      this.toastService.error(SUBMIT_ALL_REJECTED_ERROR)
    }
  }
}

/**
 * Normalise the response's `commandId` to the `string | undefined` the row field
 * actually declares, WITHOUT changing which values retire a row.
 *
 * `commandId` is not display data: `isRowRetired` reads its truthiness as
 * positive proof that a HeliosCommand exists, and no path may ever erase one. So
 * the rule here is deliberately truthiness-preserving rather than a plain
 * `typeof === 'string'` filter:
 *
 * - a non-empty string is the contract case and passes through;
 * - `''` is reported as ABSENT. It never retired a row (it is falsy), and
 *   treating it as present is what let `result.commandId ?? row.commandId`
 *   write it straight over an existing commandId — `''` is not nullish, so `??`
 *   does not fall through — erasing the UI's only record of a queued command;
 * - a truthy OFF-CONTRACT value (a number, an object) still retires the row, and
 *   is stringified so the field is a real string for `describeSubmissionStatus`
 *   and the tooltip. Dropping it instead would fail OPEN: the row would fall into
 *   the proven-rejection branch, stay editable, and one cell edit would put a
 *   possibly-queued financial command back into `submittableRows`. Over-retention
 *   costs a delete-and-re-enter; the other direction books a second command.
 * - any other falsy value (`null`, `undefined`, `0`, `false`, `NaN`) never
 *   retired a row either, so it is absent too.
 */
export function normaliseCommandId(commandId: unknown): string | undefined {
  if (typeof commandId === 'string') {
    return commandId.length > 0 ? commandId : undefined
  }
  return commandId ? String(commandId) : undefined
}

/**
 * The Submission column's tooltip content, shaped as the `key -> messages` map
 * `ProcessingErrorTooltip` renders.
 *
 * Keyed by the row's `submissionStatus`, mirroring how `applyRowResults` keys
 * `errorMessageMap` for a rejected row. Both the key and the values are
 * unvalidated server strings (`status: string`, `messages?: string[]`), and the
 * tooltip writes both through `textContent` — which is the entire reason this
 * column may not fall back to ag-grid's default tooltip component.
 *
 * Returns `undefined` when there is nothing to show, so the tooltip renders
 * nothing rather than an empty table.
 */
export function buildSubmissionMessageMap(
  row?: ProcessingGridRow,
): Map<string, string[]> | undefined {
  const messages = row?.submissionMessages ?? []
  if (messages.length === 0) {
    return undefined
  }
  return new Map<string, string[]>([
    [row?.submissionStatus ?? 'Submission', messages],
  ])
}

/**
 * True when a failed request carries a status proving the server rejected it
 * without queueing anything.
 *
 * Status 0 means no response ever arrived (network drop, CORS, abort) and 408 /
 * 5xx — notably a 504 gateway timeout — can all fire AFTER the batch was queued,
 * so none of those may be treated as a clean rejection.
 */
export function isDefiniteServerRejection(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false
  }
  return error.status >= 400 && error.status < 500 && error.status !== 408
}

/**
 * `<input type>` values that own NO paste behaviour, so an `<input>` of one of
 * these types must not suppress the container-level paste handler.
 *
 * The list exists because of `EditableCheckboxRenderer`, which renders
 * `<input ref="checkbox-input" type="checkbox">` for this grid's two flag columns
 * (`libs/ag-grid-config/src/lib/editable-checkbox-renderer/editable-checkbox-renderer.ts`).
 * A bare `tagName === 'INPUT'` test matched those, so clicking a flag checkbox —
 * which makes it the focused paste target — silently disabled Excel paste, the
 * tab's headline interaction: the next 20-row paste returned with no rows
 * appended, no `preventDefault()` and no message. A checkbox cannot receive
 * pasted text, so the guard's stated premise ("the target owns its own paste
 * behaviour") is simply false for it.
 *
 * `HTMLInputElement.type` normalises to lower case and reports an unknown value
 * as `text`, so anything not listed here is treated as text entry — which keeps
 * the guard fail-SAFE for the case it exists for: an open in-cell inline editor
 * is a text input, and a paste aimed at it must still reach it.
 */
const NON_PASTE_INPUT_TYPES: readonly string[] = [
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'range',
  'color',
]

/**
 * True for an event target that owns its own paste behaviour — an open in-cell
 * inline editor's text `<input>`, a `<textarea>`, or any contenteditable host.
 * Used to keep the container-level paste handler from hijacking a paste the user
 * aimed at a control that will consume it itself.
 *
 * The test is about what a target can RECEIVE, never its tag name. Two elements
 * on this tab are `INPUT`/`SELECT` yet cannot accept pasted text at all, and
 * both silently disabled Excel paste — the ticket's headline interaction — while
 * they were matched here:
 *
 * - `<input type="checkbox">`, rendered by `EditableCheckboxRenderer` for the
 *   flag column(s). Narrowed away by `NON_PASTE_INPUT_TYPES`.
 * - `<select>`, i.e. the command-type dropdown, which is the LEFTMOST control in
 *   the toolbar and therefore sits on the most natural path through the tab:
 *   choose the command type, then Ctrl+V a block from Excel. A `<select>` has no
 *   paste behaviour of its own to protect — pasting into one does nothing — so
 *   the premise "the target owns its own paste behaviour" was simply false for
 *   it, exactly as it was for the checkbox. While `SELECT` was matched here,
 *   `onPaste` returned at this guard with no rows appended, no
 *   `preventDefault()` and no message whatsoever: a silent failure of the
 *   tab's headline interaction, reached by the most ordinary sequence of clicks
 *   available. Do not reintroduce the term.
 *
 * `HTMLInputElement.type` normalises to lower case and reports an unknown value
 * as `text`, so an `<input>` not named in `NON_PASTE_INPUT_TYPES` is treated as
 * text entry — which keeps the guard fail-SAFE for the case it exists for: an
 * open in-cell inline editor is a text input, and a paste aimed at it must still
 * reach it rather than appending a junk row.
 *
 * The `isContentEditable` test is `=== true` rather than a bare truthiness read
 * because the property is not universally implemented — jsdom leaves it
 * `undefined` on an ordinary element — so a bare `|| target.isContentEditable`
 * RETURNED `undefined` from a function declared to return `boolean`. Every caller
 * consumes it in a truthy context, so the behaviour was correct; but a predicate
 * that can hand back a non-boolean is a trap for the next reader and for any
 * `=== false` comparison, and it went unnoticed only because this function had no
 * test at all. The explicit test makes the declared contract true.
 */
export function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  if (target.tagName === 'INPUT') {
    return !NON_PASTE_INPUT_TYPES.includes((target as HTMLInputElement).type)
  }
  return target.tagName === 'TEXTAREA' || target.isContentEditable === true
}

/** Map a registry column's data type onto the inline-edit editor's data type. */
export function toInlineEditDataType(
  dataType: ProcessingColumnDataType,
): InlineEditDataType {
  switch (dataType) {
    case 'number':
      return InlineEditDataType.Number
    case 'date':
      return InlineEditDataType.Date
    default:
      return InlineEditDataType.String
  }
}
